// src/plans/PlansView.tsx
//
// BekalNanti — Milestone P3.6: Plans / Rencana View
// Unified repository of all household plans clearly attributed to their respective person/subject.

import React, { useState } from 'react'
import { JourneyData, GoalRecord } from '../journey/types'
import { isEducationGoal, getGoalOwnerDisplay } from '../lib/goalCapability'
import { getGoalDomain } from '../lib/goalDomainRegistry'
import {
  Target,
  Plus,
  Edit3,
  GraduationCap,
  Calculator,
  Coins,
  ShieldCheck,
  Filter,
  CheckCircle2,
  AlertCircle,
  Compass,
} from 'lucide-react'

interface PlansViewProps {
  journey: JourneyData
  onOpenAddPlan: (prefillPersonId?: string | null) => void
  onEditPlan: (goal: GoalRecord) => void
  onOpenTargetSpecification: (goalId: string) => void
  onOpenCostFunding: (goalId: string) => void
  onOpenCalculation: (goalId: string) => void
  onOpenRealityCheck?: (goalId: string) => void
  onOpenPetaEsok?: (goalId: string) => void
}

export const PlansView: React.FC<PlansViewProps> = ({
  journey,
  onOpenAddPlan,
  onEditPlan,
  onOpenTargetSpecification,
  onOpenCostFunding,
  onOpenCalculation,
  onOpenRealityCheck,
  onOpenPetaEsok,
}) => {
  const { goals, persons } = journey
  const [selectedPersonFilter, setSelectedPersonFilter] = useState<string>('all')

  const activePersons = persons.filter((p) => p.status !== 'archived')

  const filteredGoals =
    selectedPersonFilter === 'all'
      ? goals
      : selectedPersonFilter === 'joint'
      ? goals.filter((g) => !g.personId)
      : goals.filter((g) => g.personId === selectedPersonFilter)

  return (
    <div className="space-y-6 text-left pb-16">
      {/* Top Banner / Heading */}
      <div className="rounded-3xl bg-gradient-to-br from-teal-50/80 via-white to-emerald-50/40 border border-teal-100/80 p-5 shadow-2xs space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-teal-200 text-[#0F766E] text-[11px] font-bold shadow-2xs">
            <Target size={14} className="text-[#0EA5A4]" />
            <span>Daftar Rencana Masa Depan</span>
          </span>

          <button
            type="button"
            onClick={() =>
              onOpenAddPlan(
                selectedPersonFilter !== 'all' && selectedPersonFilter !== 'joint'
                  ? selectedPersonFilter
                  : null
              )
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-all cursor-pointer shadow-2xs active:scale-95"
          >
            <Plus size={14} />
            <span>Tambah Rencana</span>
          </button>
        </div>

        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
            Semua Rencana Bekal ({goals.length})
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Setiap rencana memiliki penerima yang jelas. Pantau kesiapan sasaran, biaya, dan hasil perhitungannya di sini.
          </p>
        </div>
      </div>

      {/* Person Filter Tabs */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold">
          <Filter size={13} className="text-slate-400" />
          <span>Filter Berdasarkan Penerima Rencana:</span>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          <button
            type="button"
            onClick={() => setSelectedPersonFilter('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all cursor-pointer ${
              selectedPersonFilter === 'all'
                ? 'bg-[#0EA5A4] text-white border-[#0EA5A4] shadow-2xs'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            Semua ({goals.length})
          </button>

          {activePersons.map((p) => {
            const count = goals.filter((g) => g.personId === p.id).length
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedPersonFilter(p.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all cursor-pointer ${
                  selectedPersonFilter === p.id
                    ? 'bg-[#0EA5A4] text-white border-[#0EA5A4] shadow-2xs'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {p.fullName} • {count}
              </button>
            )
          })}

          <button
            type="button"
            onClick={() => setSelectedPersonFilter('joint')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all cursor-pointer ${
              selectedPersonFilter === 'joint'
                ? 'bg-[#0EA5A4] text-white border-[#0EA5A4] shadow-2xs'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            Keluarga Bersama • {goals.filter((g) => !g.personId).length}
          </button>
        </div>
      </div>

      {/* Plans List */}
      {filteredGoals.length > 0 ? (
        <div className="space-y-3.5">
          {filteredGoals.map((goal) => {
            const isEdu = isEducationGoal(goal.goalType)
            const domain = getGoalDomain(goal.goalType)
            const ownerDisplay = getGoalOwnerDisplay(goal, activePersons, journey.archivedPersons)
            const target = goal.target
            const costCount = goal.costItems?.length || 0
            const fundCount = goal.fundingSources?.length || 0

            const stageLabel = target?.targetStage
              ? domain.stages.find((s) => s.id === target.targetStage)?.label || target.targetStage
              : null

            return (
              <div
                key={goal.id}
                className="rounded-3xl border border-slate-200/90 bg-white p-5 shadow-xs space-y-4 hover:border-slate-300 transition-all text-left"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-teal-50 text-[#0F766E] border border-teal-100 flex items-center justify-center font-bold text-base flex-shrink-0 shadow-2xs">
                      {isEdu ? <GraduationCap size={20} /> : <Target size={20} />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-tight">
                          {goal.name}
                        </h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            ownerDisplay.isArchived
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-slate-100 text-slate-700 border-slate-200'
                          }`}
                        >
                          Untuk: {ownerDisplay.fullNameWithRelation}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            goal.priority === 'high'
                              ? 'bg-rose-50 text-rose-700 border border-rose-200'
                              : goal.priority === 'low'
                              ? 'bg-slate-50 text-slate-600 border border-slate-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          Prioritas {goal.priority === 'high' ? 'Tinggi' : goal.priority === 'low' ? 'Rendah' : 'Menengah'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5 capitalize">
                        Kategori: {domain.title}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onEditPlan(goal)}
                    className="p-1.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors cursor-pointer flex-shrink-0"
                    title="Ubah data rencana"
                  >
                    <Edit3 size={13} />
                  </button>
                </div>

                {/* Plan Content Details */}
                <div className="space-y-3 pt-2 border-t border-slate-100">
                  {/* Status grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {/* Sasaran */}
                    <div className="p-2.5 rounded-2xl bg-slate-50/70 border border-slate-200/70 space-y-1">
                      <div className="flex items-center justify-between text-[11px] text-slate-500 font-semibold">
                        <span>{domain.targetStageLabel}</span>
                        {target ? (
                          <span className="text-emerald-600 font-bold flex items-center gap-0.5">
                            <CheckCircle2 size={11} /> Lengkap
                          </span>
                        ) : (
                          <span className="text-amber-600 font-bold flex items-center gap-0.5">
                            <AlertCircle size={11} /> Belum dirinci
                          </span>
                        )}
                      </div>
                      {target ? (
                        <p className="text-xs font-medium text-slate-800 truncate">
                          {target.programName || stageLabel} • {target.institutionName || target.targetCity || target.targetCountry} ({target.targetYear})
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400">
                          {domain.targetStagePlaceholder}
                        </p>
                      )}
                    </div>

                    {/* Biaya & Dana */}
                    <div className="p-2.5 rounded-2xl bg-slate-50/70 border border-slate-200/70 space-y-1">
                      <div className="flex items-center justify-between text-[11px] text-slate-500 font-semibold">
                        <span>Biaya &amp; Pendanaan</span>
                        {costCount > 0 ? (
                          <span className="text-emerald-600 font-bold flex items-center gap-0.5">
                            <CheckCircle2 size={11} /> {costCount} Komponen
                          </span>
                        ) : (
                          <span className="text-amber-600 font-bold flex items-center gap-0.5">
                            <AlertCircle size={11} /> Belum ada biaya
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-slate-800">
                        {costCount > 0
                          ? `${costCount} item biaya tercatat • ${fundCount} sumber dana`
                          : 'Komponen modal & biaya belum dimasukkan.'}
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => onOpenTargetSpecification(goal.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-teal-200 bg-white text-slate-700 hover:text-[#0F766E] text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                    >
                      <Target size={13} className="text-[#0EA5A4]" />
                      <span>{target ? 'Ubah Sasaran' : 'Rincikan Sasaran'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => onOpenCostFunding(goal.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-teal-200 bg-white text-slate-700 hover:text-[#0F766E] text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                    >
                      <Coins size={13} className="text-[#0EA5A4]" />
                      <span>Biaya &amp; Dana</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => onOpenCalculation(goal.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-50 hover:bg-teal-100 border border-teal-200 text-[#0F766E] text-xs font-bold transition-all cursor-pointer shadow-2xs"
                    >
                      <Calculator size={13} className="text-[#0EA5A4]" />
                      <span>Lihat Perhitungan</span>
                    </button>

                    {onOpenRealityCheck && (
                      <button
                        type="button"
                        onClick={() => onOpenRealityCheck(goal.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                      >
                        <ShieldCheck size={13} className="text-emerald-600" />
                        <span>Reality Check</span>
                      </button>
                    )}

                    {onOpenPetaEsok && (
                      <button
                        type="button"
                        onClick={() => onOpenPetaEsok(goal.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-teal-200 bg-white hover:bg-teal-50 text-[#0F766E] text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                      >
                        <Compass size={13} className="text-[#0EA5A4]" />
                        <span>Lihat PetaEsok</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="py-12 px-6 rounded-3xl border border-dashed border-slate-200 bg-white text-center space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center mx-auto">
            <Target size={24} />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-slate-900">
              Belum Ada Rencana untuk Kategori Ini
            </h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
              Mulai tambahkan rencana baru untuk mengamankan bekal masa depan keluarga.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenAddPlan(selectedPersonFilter !== 'all' && selectedPersonFilter !== 'joint' ? selectedPersonFilter : null)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer"
          >
            <Plus size={14} />
            <span>Tambah Rencana Baru</span>
          </button>
        </div>
      )}
    </div>
  )
}
